import * as R from 'ramda';
import toast from 'Shared/toast';
import isOnLoginPage from './isOnLoginPage';

const isString = R.is(String);

const noop = R.always(undefined);

const isNotEmpty = R.complement(R.isEmpty);

// Tracks error keys already toasted on the current page so repeated
// failures (from the same or different services) don't flood the user
// with duplicate toasts. Cleared whenever the route (pathname) changes.
//
// Keyed by HTTP status code when available (e.g. every 404, regardless of
// which route/message produced it, is only toasted once per page) and
// falls back to the message text for errors with no response status
// (e.g. network/timeout errors).
const shownErrorKeys = new Set();
let lastPathname = window.location.pathname;

const resetForNewPathname = () => {
    if (window.location.pathname !== lastPathname) {
        lastPathname = window.location.pathname;
        shownErrorKeys.clear();
        toast.dismiss();
    }
};

function toastErrorOnce(message, dedupeKey) {
    const key = String(dedupeKey ?? message)
        .trim()
        .toLowerCase();
    if (shownErrorKeys.has(key)) {
        return undefined;
    }
    shownErrorKeys.add(key);
    return toast.error(message);
}

function toastIfMessage(path) {
    return [
        R.pathSatisfies(R.allPass([isString, isNotEmpty]), path),
        (error) => {
            const status = R.path(['response', 'status'], error);
            return toastErrorOnce(R.path(path, error), status);
        },
    ];
}

function errorHandler(error, showToast = true) {
    resetForNewPathname();

    // eslint-disable-next-line no-console
    console.error(error);

    if (!isOnLoginPage() && showToast) {
        // prettier-ignore
        R.cond([
            [R.pathEq(401, ['response', 'status']), noop],
            toastIfMessage(['response', 'errors', 0, 'message']),
            toastIfMessage(['response', 'data', 'message']),
            toastIfMessage(['message']),
            [R.T, (err) => toastErrorOnce('An unknown error occurred.', R.path(['response', 'status'], err))],
        ])(error)
    }
}

// Exposed for tests only, so each test can start with a clean dedupe state.
errorHandler.resetShownErrorMessages = () => shownErrorKeys.clear();

export default errorHandler;
